import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  Target, 
  Award, 
  Globe, 
  Users, 
  CheckCircle, 
  ArrowRight,
  Calendar,
  User
} from 'lucide-react';

const About = () => {
  const { t } = useTranslation('public');
  const [teamSettings, setTeamSettings] = useState({ enabled: true, title: 'Notre Team', members: [] });
  const [loading, setLoading] = useState(true);
  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    const loadTeamSettings = async () => {
      try {
        const response = await fetch(`${backendUrl}/api/site-settings/team`);
        if (response.ok) {
          const data = await response.json();
          setTeamSettings(data);
        }
      } catch (error) {
        console.error('Error loading team settings:', error);
      } finally {
        setLoading(false);
      }
    };
    loadTeamSettings();
  }, [backendUrl]);

  const values = [
    {
      icon: Target,
      title: t('about.values.excellence.title'),
      description: t('about.values.excellence.description')
    },
    {
      icon: Users,
      title: t('about.values.proximity.title'),
      description: t('about.values.proximity.description')
    },
    {
      icon: Award,
      title: t('about.values.expertise.title'),
      description: t('about.values.expertise.description')
    },
    {
      icon: Globe,
      title: t('about.values.innovation.title'),
      description: t('about.values.innovation.description')
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-brand-primary-mid to-brand-text-light text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              {teamSettings.title || t('about.heroTitle')}
            </h1>
            <p className="text-xl max-w-3xl mx-auto text-gray-100">
              {t('about.heroSubtitle')}
            </p>
          </div>
        </div>
      </section>

      {/* Team Members Section */}
      {teamSettings.members && teamSettings.members.length > 0 && (
        <section className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                {t('about.teamTitle')}
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                {t('about.teamSubtitle')}
              </p>
            </div>

            {/* Layout zigzag */}
            <div className="space-y-8">
              {teamSettings.members.map((member, index) => {
                const isEven = index % 2 === 0;
                return (
                  <div 
                    key={member.id} 
                    className={`flex flex-col md:flex-row items-stretch bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow min-h-[280px] ${
                      isEven ? '' : 'md:flex-row-reverse'
                    }`}
                  >
                    {/* Photo - taille fixe */}
                    <div className="w-full md:w-[280px] h-[280px] bg-gradient-to-br from-brand-primary-mid/20 to-brand-text-light/20 flex items-center justify-center flex-shrink-0">
                      {member.photo_url ? (
                        <img 
                          src={member.photo_url.startsWith('/api') ? `${backendUrl}${member.photo_url}` : member.photo_url}
                          alt={member.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <User size={80} className="text-gray-400" />
                      )}
                    </div>
                    
                    {/* Infos */}
                    <div className={`flex-1 p-8 flex flex-col justify-center ${isEven ? 'text-left' : 'text-left md:text-right'}`}>
                      <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">{member.name}</h3>
                      <p className="text-brand-primary-mid font-semibold text-lg mb-4">{member.position}</p>
                      {member.description && (
                        <p className="text-gray-600 leading-relaxed">{member.description}</p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Message si pas de membres */}
      {!loading && (!teamSettings.members || teamSettings.members.length === 0) && (
        <section className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <Users size={64} className="mx-auto text-gray-400 mb-4" />
            <h2 className="text-2xl font-bold text-gray-700 mb-2">{t('about.teamLoading')}</h2>
            <p className="text-gray-500">{t('about.teamLoadingSubtitle')}</p>
          </div>
        </section>
      )}

      {/* Values Section */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {t('about.valuesTitle')}
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {t('about.valuesSubtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => {
              const IconComponent = value.icon;
              return (
                <div key={index} className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-shadow p-8 text-center">
                  <div className="text-brand-primary-mid mb-4 flex justify-center">
                    <IconComponent size={48} />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">{value.title}</h3>
                  <p className="text-gray-600">{value.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                {t('about.missionTitle')}
              </h2>
              <p className="text-lg text-gray-600 mb-6">
                <strong>{t('about.missionSlogan')}</strong> {t('about.missionDescription')}
              </p>
              <p className="text-gray-600 mb-6">
                {t('about.missionText')}
              </p>
              <div className="space-y-4">
                <div className="flex items-center">
                  <CheckCircle className="text-brand-primary-mid mr-3" size={20} />
                  <span>{t('about.missionPoints.personalized')}</span>
                </div>
                <div className="flex items-center">
                  <CheckCircle className="text-brand-primary-mid mr-3" size={20} />
                  <span>{t('about.missionPoints.adapted')}</span>
                </div>
                <div className="flex items-center">
                  <CheckCircle className="text-brand-primary-mid mr-3" size={20} />
                  <span>{t('about.missionPoints.support')}</span>
                </div>
                <div className="flex items-center">
                  <CheckCircle className="text-brand-primary-mid mr-3" size={20} />
                  <span>{t('about.missionPoints.availability')}</span>
                </div>
              </div>
            </div>
            <div className="bg-gradient-to-br from-brand-primary-mid/10 to-brand-text-light/10 rounded-2xl p-8">
              <h3 className="text-2xl font-semibold text-gray-900 mb-6">
                {t('about.expertiseTitle')}
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-white rounded-lg p-4 shadow-sm">
                  <h4 className="font-semibold text-gray-900 mb-2">{t('about.expertiseAreas.individuals.title')}</h4>
                  <p className="text-sm text-gray-600">{t('about.expertiseAreas.individuals.description')}</p>
                </div>
                <div className="bg-white rounded-lg p-4 shadow-sm">
                  <h4 className="font-semibold text-gray-900 mb-2">{t('about.expertiseAreas.professionals.title')}</h4>
                  <p className="text-sm text-gray-600">{t('about.expertiseAreas.professionals.description')}</p>
                </div>
                <div className="bg-white rounded-lg p-4 shadow-sm">
                  <h4 className="font-semibold text-gray-900 mb-2">{t('about.expertiseAreas.expatriates.title')}</h4>
                  <p className="text-sm text-gray-600">{t('about.expertiseAreas.expatriates.description')}</p>
                </div>
                <div className="bg-white rounded-lg p-4 shadow-sm">
                  <h4 className="font-semibold text-gray-900 mb-2">{t('about.expertiseAreas.travel.title')}</h4>
                  <p className="text-sm text-gray-600">{t('about.expertiseAreas.travel.description')}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
            {t('about.ctaTitle')}
          </h2>
          <p className="text-xl text-gray-600 mb-8">
            {t('about.ctaSubtitle')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/rendez-vous" 
              className="bg-brand-primary-mid text-white px-8 py-4 rounded-lg font-semibold hover:bg-opacity-90 transition-colors inline-flex items-center justify-center"
            >
              <Calendar className="mr-2" size={20} />
              {t('about.ctaAppointment')}
            </Link>
            <Link 
              to="/contact" 
              className="border-2 border-brand-primary-mid text-brand-primary-mid px-8 py-4 rounded-lg font-semibold hover:bg-brand-primary-mid hover:text-white transition-colors inline-flex items-center justify-center"
            >
              {t('about.ctaContact')}
              <ArrowRight className="ml-2" size={20} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
